import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { AutomationFlowListItem } from '@neuroedge/shared';
import { Button, Card, PageState, useToast } from '../../components/ui';
import {
  useAutomationFlows,
  useCloneAutomationFlow,
  useDeleteMyAutomationFlow,
} from '../../api/automation-flows';

interface FlowGroup {
  key: string;
  system?: AutomationFlowListItem;
  mine?: AutomationFlowListItem;
}

function groupByKey(items: AutomationFlowListItem[]): FlowGroup[] {
  const groups = new Map<string, FlowGroup>();
  for (const item of items) {
    const g = groups.get(item.key) ?? { key: item.key };
    if (item.isSystem) g.system = item;
    else g.mine = item;
    groups.set(item.key, g);
  }
  return [...groups.values()].sort((a, b) =>
    (a.system?.name ?? a.mine?.name ?? '').localeCompare(b.system?.name ?? b.mine?.name ?? ''),
  );
}

/**
 * Tenant-facing view of the conversation flow library (docs/API_SPEC.md
 * section 22). Every system flow (the shared, platform-managed default) can
 * be cloned into this tenant's own editable copy - once cloned, the clone
 * (same `key`, so no automation needs to change) is what actually runs for
 * this tenant, until it's archived or deleted, at which point behavior
 * reverts to the system default.
 */
export function AutomationFlowsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const flows = useAutomationFlows({ mine: true });
  const clone = useCloneAutomationFlow();
  const del = useDeleteMyAutomationFlow();

  const groups = useMemo(() => groupByKey(flows.data?.items ?? []), [flows.data]);

  async function onClone(systemFlow: AutomationFlowListItem) {
    setBusyKey(systemFlow.key);
    try {
      const created = await clone.mutateAsync({ id: systemFlow.id, input: {} });
      toast.success(`Cloned "${systemFlow.name}" - now edit your copy.`);
      navigate(`/automation-flows/${created.id}/edit`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not clone the flow.');
    } finally {
      setBusyKey(null);
    }
  }

  async function onDelete(mine: AutomationFlowListItem) {
    if (
      !window.confirm(
        `Delete your customized copy of "${mine.name}"? Behavior for this flow will revert to the platform default.`,
      )
    ) {
      return;
    }
    setBusyKey(mine.key);
    try {
      await del.mutateAsync(mine.id);
      toast.success('Customization deleted - reverted to the platform default.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete the customization.');
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 1000 }}>
      <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Conversation flows</h1>
      <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>
        Multi-step WhatsApp conversations (like appointment booking or product ordering) that a{' '}
        <code>START_FLOW</code> automation action can start. Each one ships with a platform default;
        clone it to customize the wording or branching just for your business - your copy takes over
        automatically, no automation needs to change.
      </p>

      <Card title="Library">
        {flows.isPending ? (
          <PageState kind="loading" message="Loading flows…" />
        ) : flows.isError ? (
          <PageState
            kind="error"
            message={flows.error.message}
            onRetry={() => void flows.refetch()}
          />
        ) : groups.length === 0 ? (
          <PageState kind="empty" title="No flows yet" />
        ) : (
          <ul
            style={{
              margin: 0,
              padding: 0,
              listStyle: 'none',
              display: 'grid',
              gap: 'var(--space-3)',
            }}
          >
            {groups.map((g) => {
              const label = g.system?.name ?? g.mine?.name ?? g.key;
              const busy = busyKey === g.key && (clone.isPending || del.isPending);
              return (
                <li
                  key={g.key}
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    borderBottom: '1px solid var(--color-border)',
                    paddingBottom: 'var(--space-3)',
                  }}
                >
                  <strong>{label}</strong>
                  <span
                    style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}
                  >
                    {g.key}
                  </span>
                  {g.mine ? (
                    <>
                      <span className="ne-badge">
                        {g.mine.status === 'ACTIVE' ? 'your copy - active' : 'your copy - archived'}
                      </span>
                      <span style={{ flex: 1, minWidth: 0 }} />
                      <Button
                        variant="secondary"
                        onClick={() => navigate(`/automation-flows/${g.mine!.id}/edit`)}
                      >
                        Edit your copy
                      </Button>
                      <Button variant="secondary" disabled={busy} onClick={() => onDelete(g.mine!)}>
                        Delete customization
                      </Button>
                    </>
                  ) : (
                    <>
                      <span className="ne-badge">platform default</span>
                      <span style={{ flex: 1, minWidth: 0 }} />
                      <Button
                        variant="secondary"
                        disabled={busy || !g.system}
                        onClick={() => g.system && void onClone(g.system)}
                      >
                        {busy ? 'Cloning…' : 'Clone & customize'}
                      </Button>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/automations">Automations</Link> ·{' '}
        <Link to="/automation-templates">Automation templates</Link> ·{' '}
        <Link to="/">← Back to dashboard</Link>
      </p>
    </div>
  );
}
